import qz from "qz-tray";
import { AppConfig } from "../AppConfig";

type AndroidPrintResult = boolean | string | void;

interface AndroidEposPrinter {
  /**
   * Queues an 80mm receipt PNG with the Android app's configured kitchen
   * printer. Native implementations should return true (or "accepted") only
   * after the print job has been accepted by the printer/SDK queue.
   */
  printReceipt(base64Png: string, jobName: string): AndroidPrintResult;
  getDefaultPrinterName?(): string;
}

declare global {
  interface Window {
    EposPrinter?: AndroidEposPrinter;
  }
}

export interface PrintReceiptResult {
  transport: "android-bridge" | "wzone-web-print" | "qz-tray";
  printerName?: string;
}

interface WZoneHealthResponse {
  service?: string;
  ready?: boolean;
}

interface PrintJournalEntry {
  transport: "wzone-web-print" | "qz-tray";
  accepted?: boolean;
  printerName?: string;
  expires: number;
}

const journalKey = (jobId: string) => `wzone.print.v1:${jobId}`;
let lastJournalCleanup = 0;
const readJournal = (jobId: string): PrintJournalEntry | undefined => {
  const value = window.localStorage.getItem(journalKey(jobId));
  if (!value) return undefined;
  const record = JSON.parse(value) as PrintJournalEntry;
  if (record.expires > Date.now()) return record;
  window.localStorage.removeItem(journalKey(jobId));
  return undefined;
};
const saveJournal = (jobId: string, record: Omit<PrintJournalEntry, "expires">) => {
  // Save before dispatch: after a timeout/reload the same order must not switch
  // to another transport, which has no knowledge of the original receipt.
  if (Date.now() - lastJournalCleanup > 60 * 60 * 1000) {
    Object.keys(window.localStorage).filter(key => key.startsWith("wzone.print.v1:")).forEach(key => {
      try {
        const entry = JSON.parse(window.localStorage.getItem(key) || "null") as PrintJournalEntry | null;
        if (entry && entry.expires <= Date.now()) window.localStorage.removeItem(key);
      } catch { /* Keep corrupt history for diagnosis; do not silently permit a reprint. */ }
    });
    lastJournalCleanup = Date.now();
  }
  window.localStorage.setItem(journalKey(jobId), JSON.stringify({
    ...record, expires: Date.now() + 30 * 24 * 60 * 60 * 1000,
  }));
};

interface WZonePrintResponse {
  accepted?: boolean;
  printerName?: string;
  error?: string;
}

const wZoneUrl = (AppConfig.WZoneWebPrintUrl || "").replace(/\/$/, "");

const fetchWithTimeout = async (
  input: RequestInfo,
  init: RequestInit,
  timeoutMs: number
) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
};

const isWZoneAvailable = async () => {
  if (!wZoneUrl) return false;
  try {
    const response = await fetchWithTimeout(
      `${wZoneUrl}/api/v1/health`,
      { method: "GET", cache: "no-store" },
      800
    );
    if (!response.ok) return false;
    const health = (await response.json()) as WZoneHealthResponse;
    return health.service === "WZoneWebPrint";
  } catch {
    return false;
  }
};

const printWithWZone = async (
  base64Png: string,
  jobName: string,
  jobId: string
): Promise<PrintReceiptResult | undefined> => {
  const prior = readJournal(jobId);
  if (prior?.transport !== "wzone-web-print" && !(await isWZoneAvailable())) return undefined;
  saveJournal(jobId, { transport: "wzone-web-print" });

  // Once health succeeds, do not fall through to QZ on an ambiguous network
  // error: retrying this same durable job ID is safer than double printing.
  const response = await fetchWithTimeout(
    `${wZoneUrl}/api/v1/print`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-WZone-Client": "epos.client",
      },
      body: JSON.stringify({ jobId, jobName, base64Png }),
    },
    15000
  );
  const result = (await response.json()) as WZonePrintResponse;
  if (!response.ok || !result.accepted)
    throw new Error(result.error || "WZoneWebPrint rejected the receipt job.");

  saveJournal(jobId, { transport: "wzone-web-print", accepted: true, printerName: result.printerName });
  return {
    transport: "wzone-web-print",
    printerName: result.printerName,
  };
};

const isAndroid = () => /Android/i.test(window.navigator.userAgent);

export const hasAndroidPrintBridge = () =>
  typeof window.EposPrinter?.printReceipt === "function";

export const getAutomaticPrintingSetupIssue = () => {
  if (
    isAndroid() &&
    !hasAndroidPrintBridge() &&
    !AppConfig.QzPrintServerHost
  )
    return "Automatic printing is not configured on this Android device. Open the orders page in the EPOS Android app with its EposPrinter bridge enabled, or configure QzPrintServerHost.";

  return undefined;
};

const nativeResultWasAccepted = (result: AndroidPrintResult) => {
  if (result === false) return false;
  if (typeof result !== "string") return true;

  return !["false", "failed", "error", "rejected"].includes(
    result.trim().toLowerCase()
  );
};

/**
 * Sends a rendered receipt to the best available silent-print transport.
 * Android WebView apps use the native EposPrinter bridge; desktop browsers
 * and Android devices configured for a remote QZ print server use QZ Tray.
 */
export const printReceiptImage = async (
  base64Png: string,
  jobName: string,
  jobId = jobName
): Promise<PrintReceiptResult> => {
  if (hasAndroidPrintBridge()) {
    const result = await Promise.resolve(
      window.EposPrinter!.printReceipt(base64Png, jobName)
    );
    if (!nativeResultWasAccepted(result))
      throw new Error("The Android printer rejected the receipt job.");

    return {
      transport: "android-bridge",
      printerName: window.EposPrinter?.getDefaultPrinterName?.(),
    };
  }

  if (isAndroid() && !AppConfig.QzPrintServerHost)
    throw new Error(getAutomaticPrintingSetupIssue());

  const prior = readJournal(jobId);
  if (prior?.accepted) return { transport: prior.transport, printerName: prior.printerName };
  if (prior?.transport === "qz-tray")
    throw new Error("The previous QZ print attempt has an uncertain result. Check the printer before manually reprinting this order.");

  const wZoneResult = await printWithWZone(base64Png, jobName, jobId);
  if (wZoneResult) return wZoneResult;

  if (!qz.websocket.isActive()) {
    await qz.websocket.connect(
      AppConfig.QzPrintServerHost
        ? { host: AppConfig.QzPrintServerHost }
        : undefined
    );
  }

  const defaultPrinter = await qz.printers.getDefault();
  if (!defaultPrinter) throw new Error("Default printer not found.");

  const config = qz.configs.create(defaultPrinter);
  saveJournal(jobId, { transport: "qz-tray" });
  await qz.print(config, [
    { type: "image", format: "base64", data: base64Png },
  ]);
  saveJournal(jobId, { transport: "qz-tray", accepted: true, printerName: defaultPrinter });

  return {
    transport: "qz-tray",
    printerName: defaultPrinter,
  };
};
